
import { Injectable, inject, DOCUMENT } from '@angular/core';
import { ThemeFacade } from '../state/theme/facades/theme.facade';
import { ThemeName } from '../state/theme/actions/theme-ui.actions';

/**
 * IMPORTANT:
 * localStorage key mirrored on every theme change to apply the theme before 
 * Angular boots, avoiding a flash of the light theme. 
 * keep this value in sync with index.html
 */
export const THEME_STORAGE_KEY = 'apgap-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly themeFacade = inject(ThemeFacade);

  /**
   * syncs store/localStorage/OS state and applies the active
   * theme to <html data-theme> and the flat localStorage key. 
   * Invoked once at bootstrap via provideAppInitializer.
   */
  init(): void {
    // mirror the canonical NgRx theme to the DOM and the flat anti-flash key
    this.themeFacade.theme$.subscribe((theme) => this.applyTheme(theme));

    // seed from / stay in sync with the OS preference until the user overrides
    const media = this.matchMedia();
    if (media) {
      this.themeFacade.setSystemPreference(media.matches);
      media.addEventListener('change', (event) => this.themeFacade.setSystemPreference(event.matches));
    }
  }

  private applyTheme(theme: ThemeName): void {
    this.document.documentElement.setAttribute('data-theme', theme);
    try {
      this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage may be unavailable (private mode / SSR); the app still works.
    }
  }

  private matchMedia(): MediaQueryList | null {
    const view = this.document.defaultView;
    return view?.matchMedia ? view.matchMedia(DARK_QUERY) : null;
  }
}
